export type GeolocationPermission = 'granted' | 'prompt' | 'denied' | 'unsupported'

export interface Coords {
  latitude: number
  longitude: number
}

export function isValidCoords(value: unknown): value is Coords {
  if (typeof value !== 'object' || value === null) return false
  const { latitude, longitude } = value as Partial<Coords>
  return (
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    !(latitude === 0 && longitude === 0)
  )
}

// Reads the current permission state WITHOUT triggering a browser prompt.
export async function readGeolocationPermission(): Promise<GeolocationPermission> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    return 'unsupported'
  }
  // Permissions API is optional; when missing we cannot know the state without prompting.
  if (!('permissions' in navigator) || typeof navigator.permissions.query !== 'function') {
    return 'prompt'
  }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    return status.state
  } catch {
    return 'prompt'
  }
}

// Requests the current position, prompting the user if needed. Rejects on denial/error.
export async function requestCurrentPosition(): Promise<Coords> {
  return new Promise<Coords>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      (error) => reject(new Error(`Geolocation failed (code ${error.code}): ${error.message}`)),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 5 * 60 * 1000 },
    )
  })
}
